import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, X } from 'lucide-react';
import { useFavorites } from '../../lib/favorites';
import { FavIcon, favColor } from './favUi';

// Köşe çarkı: favoriler çeyrek daire yayında dönerek açılır.
const RINGS = [{ r: 96, n: 3 }, { r: 160, n: 5 }, { r: 224, n: 7 }, { r: 288, n: 9 }];
const place = (i, total) => {
  let k = i;
  let left = total;
  for (const ring of RINGS) {
    const cnt = Math.min(ring.n, left);
    if (k < ring.n) {
      const a = (Math.PI / 2) * (cnt === 1 ? 0.5 : k / (cnt - 1));
      return { x: -Math.cos(a) * ring.r, y: -Math.sin(a) * ring.r };
    }
    k -= ring.n;
    left -= ring.n;
  }
  return null;
};

export function FavoritesWheel() {
  const { items, catalog } = useFavorites();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const visible = items.filter((f) => catalog[f.path]).slice(0, 24);
  const hoverAt = useRef(0);
  if (!visible.length) return null;
  const go = (p) => { setOpen(false); navigate(p); };
  const hitR = RINGS.find((_, j) => visible.length <= RINGS.slice(0, j + 1).reduce((s, r) => s + r.n, 0)).r + 90;
  const enter = (e) => { if (e.pointerType === 'mouse') { hoverAt.current = Date.now(); setOpen(true); } };
  const leave = (e) => { if (e.pointerType === 'mouse') setOpen(false); };
  const click = () => setOpen((o) => (Date.now() - hoverAt.current < 700 ? true : !o));
  const tapOpen = open && Date.now() - hoverAt.current > 700;
  return (
    <div className="fixed bottom-40 right-5 z-40" onPointerEnter={enter} onPointerLeave={leave} data-testid="fav-wheel">
      {tapOpen && <div className="fixed inset-0" onClick={() => setOpen(false)} data-testid="fav-wheel-backdrop" />}
      <AnimatePresence>
        {open && (
          <div key="hit" className="absolute rounded-tl-full" style={{ right: -20, bottom: -20, width: hitR, height: hitR }} />
        )}
        {open && (
          <motion.div key="ring" className="absolute left-1/2 top-1/2 w-0 h-0" initial={{ rotate: -100 }} animate={{ rotate: 0 }} exit={{ rotate: -100, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 160, damping: 16 }} style={{ transformOrigin: '0 0' }}>
            {visible.map((f, i) => {
              const pos = place(i, visible.length);
              return (
                <motion.button key={f.path} type="button" onClick={() => go(f.path)} title={f.label}
                  initial={{ x: 0, y: 0, scale: 0 }} animate={{ x: pos.x, y: pos.y, scale: 1 }} exit={{ x: 0, y: 0, scale: 0 }}
                  transition={{ delay: i * 0.025, type: 'spring', stiffness: 260, damping: 20 }}
                  className="group absolute -ml-6 -mt-6 left-0 top-0" data-testid={`fav-wheel-item-${i}`}>
                  <span className={`h-12 w-12 rounded-full bg-gradient-to-br ${favColor(f.path)} text-white shadow-lg ring-4 ring-white dark:ring-slate-900 flex items-center justify-center transition-transform group-hover:scale-110`}>
                    <FavIcon fav={f} catalog={catalog} />
                  </span>
                  <span className="pointer-events-none absolute right-full mr-2 top-1/2 -translate-y-1/2 whitespace-nowrap rounded-md bg-slate-900 text-white text-xs font-medium px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity">{f.label}</span>
                </motion.button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
      <button type="button" onClick={click} aria-label="Favoriler"
        className="relative h-14 w-14 rounded-full bg-slate-900 dark:bg-white text-amber-400 dark:text-amber-500 shadow-2xl flex items-center justify-center transition-transform hover:scale-105 active:scale-95" data-testid="fav-wheel-toggle">
        <motion.span animate={{ rotate: open ? 135 : 0 }} transition={{ type: 'spring', stiffness: 200, damping: 14 }} className="flex">
          {open ? <X className="h-6 w-6" /> : <Star className="h-6 w-6 fill-current" />}
        </motion.span>
        {!open && <span className="absolute -top-1 -right-1 h-5 min-w-5 px-1 rounded-full bg-amber-400 text-slate-900 text-[10px] font-black flex items-center justify-center">{visible.length}</span>}
      </button>
    </div>
  );
}
